import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useLanguage, LANGS } from "../i18n/LanguageContext";

export const SITE_URL = "https://fadosamesa.ch";
export const OG_IMAGE = `${SITE_URL}/images/og-image.jpg`;
export const LANG_HREFLANG = { pt: "pt", de: "de-CH", fr: "fr-CH", it: "it-CH", en: "en" };
export const OG_LOCALE = { pt: "pt_PT", de: "de_CH", fr: "fr_CH", it: "it_CH", en: "en_GB" };

const setMeta = (attr, key, content) => {
    let el = document.head.querySelector(`meta[${attr}="${key}"]`);
    if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        document.head.appendChild(el);
    }
    el.setAttribute("content", content);
};

const setLink = (rel, hreflang, href) => {
    const sel = hreflang ? `link[rel="${rel}"][hreflang="${hreflang}"]` : `link[rel="${rel}"]`;
    let el = document.head.querySelector(sel);
    if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        if (hreflang) el.setAttribute("hreflang", hreflang);
        document.head.appendChild(el);
    }
    el.setAttribute("href", href);
};

export const useSEO = ({ title, description, keywords, jsonLd, robots }) => {
    const { lang, t } = useLanguage();
    const { pathname } = useLocation();
    const jsonStr = jsonLd ? JSON.stringify(jsonLd) : null;

    useEffect(() => {
        const pageUrl = `${SITE_URL}${pathname}?lang=${lang}`;
        document.title = title;
        setMeta("name", "description", description);
        setMeta("name", "keywords", keywords || t.seo.keywords);
        setMeta("name", "robots", robots || "index, follow, max-image-preview:large");
        setMeta("property", "og:title", title);
        setMeta("property", "og:description", description);
        setMeta("property", "og:url", pageUrl);
        setMeta("property", "og:image", OG_IMAGE);
        setMeta("property", "og:type", "website");
        setMeta("property", "og:site_name", "Fados à Mesa");
        setMeta("property", "og:locale", OG_LOCALE[lang] || "pt_PT");
        setMeta("name", "twitter:card", "summary_large_image");
        setMeta("name", "twitter:title", title);
        setMeta("name", "twitter:description", description);
        setMeta("name", "twitter:image", OG_IMAGE);
        setLink("canonical", null, pageUrl);
        document.querySelectorAll('link[rel="alternate"][hreflang]').forEach((n) => n.remove());
        LANGS.forEach((l) => setLink("alternate", LANG_HREFLANG[l], `${SITE_URL}${pathname}?lang=${l}`));
        setLink("alternate", "x-default", `${SITE_URL}${pathname}`);

        let ld = document.getElementById("ld-page");
        const finalLd = [
            {
                "@context": "https://schema.org",
                "@type": "WebPage",
                name: title,
                description: description,
                url: pageUrl,
                inLanguage: lang,
                isPartOf: { "@type": "WebSite", name: "Fados à Mesa", url: SITE_URL },
                about: { "@type": "Organization", name: "Fados à Mesa", url: SITE_URL },
            },
            ...(Array.isArray(jsonLd) ? jsonLd : jsonLd ? [jsonLd] : []),
        ];
        if (finalLd.length) {
            if (!ld) {
                ld = document.createElement("script");
                ld.type = "application/ld+json";
                ld.id = "ld-page";
                document.head.appendChild(ld);
            }
            ld.textContent = JSON.stringify(finalLd);
        } else if (ld) {
            ld.remove();
        }
        return () => { document.getElementById("ld-page")?.remove(); };
    }, [title, description, keywords, jsonStr, lang, pathname, t]);
};

export const breadcrumbLd = (items) => ({
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, i) => ({
        "@type": "ListItem",
        position: i + 1,
        name: it.name,
        item: `${SITE_URL}${it.path}`,
    })),
});

export const eventLd = (performers) => ({
    "@context": "https://schema.org",
    "@type": "MusicEvent",
    name: "Fados à Mesa — 1ª Edição",
    description:
        "Jantar-espetáculo com Fado ao vivo, gastronomia e vinhos portugueses. Uma noite portuguesa servida à mesa: porto de honra, jantar completo e três momentos de Fado.",
    image: [OG_IMAGE, `${SITE_URL}/images/rafael-pacheco-bg.webp`],
    startDate: "2026-11-14T17:00:00+01:00",
    endDate: "2026-11-15T01:30:00+01:00",
    eventStatus: "https://schema.org/EventScheduled",
    eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
    location: {
        "@type": "Place",
        name: "Centro Papa Giovanni",
        address: {
            "@type": "PostalAddress",
            streetAddress: "Hübelistrasse 55",
            postalCode: "6020",
            addressLocality: "Emmenbrücke",
            addressRegion: "Luzern",
            addressCountry: "CH",
        },
        geo: { "@type": "GeoCoordinates", latitude: 47.0705647, longitude: 8.2848735 },
    },
    organizer: {
        "@type": "Organization",
        name: "Fados à Mesa",
        url: SITE_URL,
        email: "produktion@fadosamesa.ch",
        sameAs: ["https://www.instagram.com/fadosamesa/", "https://www.facebook.com/profile.php?id=61594181040230"],
    },
    performer: performers.map((p) => ({ "@type": "Person", name: p.name })),
    offers: [
        { "@type": "Offer", name: "Standard — Early Bird", price: "119.90", priceCurrency: "CHF", category: "early-bird", url: `${SITE_URL}/bilhetes`, availability: "https://schema.org/PreOrder", validFrom: "2026-09-24T00:00:00+02:00" },
        { "@type": "Offer", name: "Standard", price: "129.90", priceCurrency: "CHF", url: `${SITE_URL}/bilhetes`, availability: "https://schema.org/PreOrder", validFrom: "2026-09-24T00:00:00+02:00" },
    ],
    inLanguage: ["pt", "de", "fr", "it", "en"],
});

export const organizationLd = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "Fados à Mesa",
    url: SITE_URL,
    logo: `${SITE_URL}/images/logo.png`,
    email: "produktion@fadosamesa.ch",
    address: { "@type": "PostalAddress", addressCountry: "CH" },
};

export const websiteLd = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "Fados à Mesa",
    url: SITE_URL,
    inLanguage: ["pt", "de-CH", "fr-CH", "it-CH", "en"],
};


export const faqLd = (items) => ({
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map((i) => ({
        "@type": "Question",
        name: i.q,
        acceptedAnswer: { "@type": "Answer", text: i.a },
    })),
});

export const venueLd = {
    "@context": "https://schema.org",
    "@type": "EventVenue",
    name: "Centro Papa Giovanni",
    address: {
        "@type": "PostalAddress",
        streetAddress: "Hübelistrasse 55",
        postalCode: "6020",
        addressLocality: "Emmenbrücke",
        addressRegion: "Luzern",
        addressCountry: "CH",
    },
    geo: { "@type": "GeoCoordinates", latitude: 47.0705647, longitude: 8.2848735 },
};

export const personLd = (artist, role) => ({
    "@context": "https://schema.org",
    "@type": "Person",
    name: artist.name,
    jobTitle: role,
    url: `${SITE_URL}/artistas/${artist.slug}`,
    image: `${SITE_URL}${artist.image}`,
    sameAs: [artist.instagram, artist.facebook].filter(Boolean),
});
